import { useEffect, useRef, useState } from "react";
import { QuerySpec } from "@src/types/dashboards";
import { getWidgetDataSource } from "./widget-query";
import { useRefetchWhenVisible } from "@src/hooks/use-check-widget-in-viewport";
import { getQueryTimeseriesForNodeUseCase } from "@src/cloud-connect/use-cases/get-query-timeseries-for-node";
import { getActiveTimeseriesUseCase } from "@src/cloud-connect/timeseries/use-cases/get-active-timeseries-use-case";
import { LINE_CHART_DEFAULT_DURATION_MS, resolvePollingMs } from "./constants";

export interface WidgetTimeseries {
  times: string[];
  values: (number | null)[];
  /** Aggregated-line: per-bucket minimums (one entry per timeslot).
   *  Undefined for raw mode or when the API didn't return aggregated_values.
   *  Used by the renderer when semantics.show_min is true. */
  minValues?: (number | null)[];
  /** Aggregated-line: per-bucket maximums. Mirror of minValues for
   *  semantics.show_max. */
  maxValues?: (number | null)[];
  /** Node display name (falls back to the node id). */
  nodeName: string;
  /** Breadcrumbs from active-timeseries `node_path.parent_names`, joined with "/". */
  breadcrumbs: string;
  isLoading: boolean;
  hasNoData: boolean;
  error?: string;
}

interface TimeseriesPoint {
  time: string;
  value: number | string | null;
}

interface AggregatedTimeseriesPoint {
  time: string;
  v_min: number | string | null;
  v_avg: number | string | null;
  v_max: number | string | null;
  v_count: number | string | null;
}

/**
 * Fetches a full time-series for a widget configured with machine+node
 * (query.source = machine, bindings[0].address = node). Same endpoints as the
 * gauge (v3 tsdata + active-timeseries for name/breadcrumbs) but maps ALL
 * values, not just the last. Preview mode skips fetching entirely.
 */
export const useWidgetTimeseries = (
  query: QuerySpec | null | undefined,
  {
    from,
    to,
    durationMs,
    frequency,
    preview,
    inViewport = true,
  }: {
    from?: string;
    to?: string;
    /** Rolling relative window: from = now − durationMs (overrides `from`). */
    durationMs?: number | null;
    frequency?: string;
    preview?: boolean;
    /** When false (off-screen widget), ticks skip fetching — the interval
     *  keeps running on its original schedule; re-entry refetches once via
     *  the visibility bridge, so kept data is never stale for long. */
    inViewport?: boolean;
  } = {},
): WidgetTimeseries => {
  const [times, setTimes] = useState<string[]>([]);
  const [values, setValues] = useState<(number | null)[]>([]);
  const [minValues, setMinValues] = useState<(number | null)[] | undefined>(
    undefined,
  );
  const [maxValues, setMaxValues] = useState<(number | null)[] | undefined>(
    undefined,
  );
  const [nodeName, setNodeName] = useState("");
  const [breadcrumbs, setBreadcrumbs] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasNoData, setHasNoData] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const dataSource = getWidgetDataSource(query);

  // Visibility bridge: latestLoadRef holds the most recent fetch so the
  // re-entry refetch calls current logic; inViewportRef feeds the tick guard
  // with fresh visibility without re-running this effect.
  const latestLoadRef = useRef<(() => void) | null>(null);
  const inViewportRef = useRefetchWhenVisible(inViewport, latestLoadRef);

  useEffect(() => {
    setTimes([]);
    setValues([]);
    setMinValues(undefined);
    setMaxValues(undefined);
    setNodeName("");
    setBreadcrumbs("");
    setHasNoData(false);
    setIsLoading(false);
    setError(undefined);

    if (preview || !dataSource) {
      latestLoadRef.current = null;
      return;
    }
    let cancelled = false;
    // Overlap guard: a slow endpoint must never stack parallel fetches when
    // the interval fires again mid-request.
    let inFlight = false;
    // Only the first (effect-driven) load shows the spinner; background
    // refreshes replace data in place so the chart never unmounts.
    let isInitialLoad = true;

    const load = async () => {
      if (inFlight || document.hidden || !inViewportRef.current) return;
      inFlight = true;
      if (isInitialLoad) {
        isInitialLoad = false;
        setIsLoading(true);
      }
      try {
        // Rolling relative window (duration) wins; then explicit from/to; else 1h.
        const toIso = to ?? new Date().toISOString();
        const fromIso =
          durationMs != null
            ? new Date(Date.now() - durationMs).toISOString()
            : from ??
              new Date(
                Date.now() - LINE_CHART_DEFAULT_DURATION_MS,
              ).toISOString();

        const [seriesResult, activeResult] = await Promise.all([
          getQueryTimeseriesForNodeUseCase({
            group: dataSource.group,
            source: dataSource.source,
            frequency:
              (frequency as
                | "raw"
                | "quarterhourly"
                | "hourly"
                | "daily"
                | "weekly"
                | "monthly"
                | "yearly") ?? "raw",
            from: fromIso,
            to: toIso,
          }),
          // Breadcrumbs + node name come from active-timeseries (tsdata has no name/paths).
          getActiveTimeseriesUseCase(dataSource.group),
        ]);
        if (cancelled) return;

        if (seriesResult.success && seriesResult.data) {
          // A later tick succeeding must clear an earlier tick's error.
          setError(undefined);
          // The /api/v3 tsdata endpoint returns one of two shapes:
          //   - raw (frequency="raw"):
          //       { values: [{ time, value }, ...] }
          //   - aggregated (hourly|quarterhourly|daily|...):
          //       { aggregated_values: [{ time, v_min, v_avg, v_max, v_count }, ...] }
          // The previous version only read `values`, so aggregated responses
          // came back as empty arrays and the chart rendered "no data" even
          // though the API had returned points. v_avg is the standard aggregate
          // metric for a line chart (central tendency per bucket); v_min/v_max
          // are the bounds that get drawn when semantics.show_min/show_max
          // are true. They live on the hook return so the renderer can opt-in
          // without re-fetching.
          const data = seriesResult.data as {
            values?: TimeseriesPoint[];
            aggregated_values?: AggregatedTimeseriesPoint[];
          };
          const aggregated = data.aggregated_values;
          const raw = data.values;
          if (aggregated && aggregated.length > 0) {
            setTimes(aggregated.map((p) => p.time));
            setValues(
              aggregated.map((p) =>
                typeof p.v_avg === "number" ? p.v_avg : null,
              ),
            );
            // Expose v_min / v_max as parallel arrays — they line up 1:1
            // with `times` and `values`, so the renderer can overlay them
            // without a zip step. null entries (off-the-chart buckets)
            // already pass through.
            setMinValues(
              aggregated.map((p) =>
                typeof p.v_min === "number" ? p.v_min : null,
              ),
            );
            setMaxValues(
              aggregated.map((p) =>
                typeof p.v_max === "number" ? p.v_max : null,
              ),
            );
            setHasNoData(false);
          } else if (raw && raw.length > 0) {
            setTimes(raw.map((p) => p.time));
            setValues(
              raw.map((p) => (typeof p.value === "number" ? p.value : null)),
            );
            // raw path: no aggregate bounds exist. Don't leave stale bounds
            // from a previous aggregated call in state — clear them so the
            // renderer can't accidentally draw a phantom min/max line on a
            // raw-mode widget.
            setMinValues(undefined);
            setMaxValues(undefined);
            setHasNoData(false);
          } else {
            setMinValues(undefined);
            setMaxValues(undefined);
            setHasNoData(true);
          }
        } else if (!seriesResult.success) {
          setError(seriesResult.error);
          setHasNoData(true);
        }

        // Resolve name + breadcrumbs from the active-timeseries payload.
        if (activeResult.success && activeResult.data) {
          const entry = Object.values(activeResult.data.timeseries).find(
            (t) => t.source === dataSource.source,
          );
          if (entry) {
            setNodeName(entry.name ?? dataSource.source);
            setBreadcrumbs(entry.node_path.parent_names.join("/"));
          }
        }
      } catch (e) {
        // A thrown fetch must still surface on the widget (mirrors the
        // gauge hook's pattern), not vanish as an unhandled rejection.
        if (!cancelled) setError(String(e));
      } finally {
        // Always release the guard — a rejected fetch must never strand the
        // interval (it would block every later tick forever).
        if (!cancelled) {
          setIsLoading(false);
          inFlight = false;
        }
      }
    };

    // Expose the latest fetch to the visibility bridge (re-entry refetch).
    latestLoadRef.current = load;
    load();
    // Refresh cadence: raw → user's polling_interval; aggregated → bucket size.
    const pollingMs = resolvePollingMs(
      frequency ?? query?.sampling?.frequency,
      query?.sampling?.polling_interval,
    );
    const intervalId =
      pollingMs != null ? setInterval(() => void load(), pollingMs) : undefined;
    return () => {
      cancelled = true;
      if (intervalId != null) clearInterval(intervalId);
      latestLoadRef.current = null;
    };
  }, [
    dataSource?.group,
    dataSource?.source,
    from,
    to,
    durationMs,
    frequency,
    preview,
    query?.sampling?.frequency,
    query?.sampling?.polling_interval,
  ]);

  return {
    times,
    values,
    minValues,
    maxValues,
    nodeName,
    breadcrumbs,
    isLoading,
    hasNoData,
    error,
  };
};
